/**
 * Onboarding API — step tracking, tip dismissal.
 */

import { apiRequest } from "./client";
import { getOnboardingLocalStateUser } from "@/lib/onboarding-local-state";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";

// Serialize every server write to onboarding_step. Concurrent writes raced
// last-write-wins on the backend and could commit an EARLIER step after a
// later one (2026-07-08 prod: five PATCHes in one second regressed the
// step). One chain at the API boundary orders ALL writers — the store,
// desktop bridge, and the web overlay's named update —
// without changing any caller's semantics. Each caller still receives its
// own write's result/rejection; a failed write never wedges the chain.
let stepWriteChain: Promise<void> = Promise.resolve();

function enqueueStepWrite<T>(
  write: (requestGuard: () => boolean) => Promise<T>,
  progressGuard?: () => boolean,
): Promise<T> {
  const userId = getOnboardingLocalStateUser();
  const authScope = getDesktopChatAuthScope();
  const requestGuard = () =>
    getOnboardingLocalStateUser() === userId &&
    (!authScope || isCurrentDesktopChatAuthScope(authScope)) &&
    (!progressGuard || progressGuard());
  const result = stepWriteChain.then(() => {
    if (!requestGuard()) throw new Error("Onboarding request scope changed");
    return write(requestGuard);
  });
  stepWriteChain = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

export type OnboardingStep =
  // web onboarding (untouched per spec §3.9)
  | "orient"
  | "tour"
  | "tour_done"
  | "install" // legacy — kept for backward compat
  | "companion_install"
  | "cli_setup"
  | "agent_frame_spotlight"
  | "demo_prompt"
  | "demo_running"
  | "demo_done"
  // desktop onboarding (added per spec §3.6)
  | "desktop_intro"
  | "desktop_perm_hotkeys"
  | "desktop_perm_voice"
  | "desktop_perm_voice_quality"
  | "desktop_perm_vision"
  | "desktop_permissions" // legacy — kept for backward compat
  | "desktop_usage_primer"
  | "desktop_intake"
  | "desktop_runtime_setup"
  | "desktop_browser_setup"
  | "desktop_founder_contact"
  | "desktop_tour" // legacy — kept for backward compat
  | "desktop_demo" // legacy — kept for backward compat
  // terminal states
  | "completed"
  | "skipped";

export function updateStep(
  step: OnboardingStep,
  desktopOnboardingVersion?: number,
  progressGuard?: () => boolean,
): Promise<void> {
  const body =
    desktopOnboardingVersion === undefined
      ? { step }
      : { step, desktop_onboarding_version: desktopOnboardingVersion };
  return enqueueStepWrite(
    (requestGuard) =>
      apiRequest("/api/v1/auth/onboarding/", {
        method: "PATCH",
        body,
        silent: true,
        requestGuard,
        signal: AbortSignal.timeout(30_000),
      }),
    progressGuard,
  );
}

export function updateStepWithName(
  step: OnboardingStep,
  name: string,
): Promise<void> {
  return enqueueStepWrite((requestGuard) =>
    apiRequest("/api/v1/auth/onboarding/", {
      method: "PATCH",
      body: { step, name },
      silent: true,
      requestGuard,
      signal: AbortSignal.timeout(30_000),
    }),
  );
}

export async function dismissTip(tipId: string): Promise<void> {
  await apiRequest("/api/v1/auth/onboarding/dismiss-tip/", {
    method: "POST",
    body: { tip_id: tipId },
    silent: true,
  });
}

export type OnboardingRestartReason = "manual" | "content_version";

// Reset target is intentionally narrow: only the two real entry points
// for the web and desktop flows. Widening to all OnboardingStep values
// would let a future caller accidentally reset users to "completed" or
// any other terminal state. (The backend additionally accepts the legacy
// `desktop_permissions` still sent by old shipped desktop builds.)
export function resetOnboarding(
  step: "orient" | "desktop_intro",
  reason: OnboardingRestartReason,
  progressGuard?: () => boolean,
): Promise<void> {
  return enqueueStepWrite(
    (requestGuard) =>
      apiRequest("/api/v1/auth/onboarding/reset/", {
        method: "POST",
        body: { step, reason },
        silent: true,
        requestGuard,
        signal: AbortSignal.timeout(30_000),
      }),
    progressGuard,
  );
}
